import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const UpdateDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchUpdateDetails();
    }
  }, [slug]);

  const fetchUpdateDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = `${API_ENDPOINTS.UPDATES}?slug=${encodeURIComponent(slug)}`;
      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.data) {
        setUpdate(response.data.data);
      } else {
        setError(response.data.message || 'Update not found');
      }
    } catch (err) {
      console.error('Error fetching update:', err);
      if (err.response) {
        if (err.response.status === 404) {
          setError('Update not found');
        } else {
          setError(err.response.data?.message || 'Failed to load update');
        }
      } else {
        setError('Cannot connect to the server. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'news': return 'bi-newspaper';
      case 'blog': return 'bi-pencil';
      case 'gallery': return 'bi-images';
      case 'event': return 'bi-calendar-event';
      default: return 'bi-file-text';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'news': return 'News';
      case 'blog': return 'Blog';
      case 'gallery': return 'Gallery';
      case 'event': return 'Event';
      default: return 'Update';
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading update...</p>
      </div>
    );
  }

  if (error || !update) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Update Not Found</h4>
          <p className="mb-0">{error || 'The requested update could not be found.'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/updates')}>
          <i className="bi bi-arrow-left me-2"></i> Back to News & Updates
        </button>
      </div>
    );
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost/sikom-ngo-website/backend';
  const imageUrl = update.image_path
    ? (update.image_path.startsWith('http') ? update.image_path : `${apiBaseUrl}/${update.image_path.replace(/^\.\.\//, '')}`)
    : null;

  return (
    <div className="update-details-page">
      {/* Hero Section */}
      <section
        className="position-relative text-white py-5"
        style={{
          background: imageUrl
            ? `linear-gradient(rgba(13, 110, 253, 0.8), rgba(13, 110, 253, 0.8)), url(${imageUrl}) center/cover`
            : 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)'
        }}
      >
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <button
            className="btn btn-outline-light mb-4"
            onClick={() => navigate('/updates')}
          >
            <i className="bi bi-arrow-left me-2"></i> Back to News & Updates
          </button>

          <div className="mb-3">
            <span className={`badge bg-light text-dark p-2`}>
              <i className={`bi ${getTypeIcon(update.type)} me-1`}></i>
              {getTypeLabel(update.type)}
            </span>
          </div>

          <h1 className="display-5 fw-bold mb-3">{update.title}</h1>

          <div className="d-flex flex-wrap gap-4 text-white-50">
            {update.author && (
              <span>
                <i className="bi bi-person me-1"></i> {update.author}
              </span>
            )}
            <span>
              <i className="bi bi-calendar me-1"></i> {formatDate(update.published_at || update.created_at)}
            </span>
            {(update.views || update.views === 0) && (
              <span>
                <i className="bi bi-eye me-1"></i> {update.views} views
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {update.excerpt && (
                <p className="lead text-muted mb-4">{update.excerpt}</p>
              )}

              {update.content ? (
                <div
                  className="content-html"
                  dangerouslySetInnerHTML={{ __html: update.content }}
                />
              ) : (
                <p className="text-muted">No content available for this update.</p>
              )}

              <hr className="my-5" />
              <Link to="/updates" className="btn btn-primary">
                <i className="bi bi-arrow-left me-2"></i> Back to All Updates
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpdateDetails;
